import { useCallback, useMemo, useState } from 'react'

import { randomIntFromInterval } from '~/bootcamp/Activities/utils/activities.utils'
import { shuffle } from '~/utils'

interface UseFractionDivideZeroReturnType {
  numerator: number
  options: Array<number | 'undefined'>
  formValid: boolean
  selectedAnswer?: string | number
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

const DIFFICULTY_LEVEL_RANGE_MAP: { [key: number]: { min: number; max: number; variation: number } } = {
  1: { min: 2, max: 8, variation: 7 },
  2: { min: 8, max: 20, variation: 13 },
}

interface QuestionGeneratorResponseModel {
  numerator: number
  variation: number
  options: Array<number | 'undefined'>
}

function getFractionDivideZeroQuestionData(
  difficultyLevel: number,
  excludes?: number[],
): QuestionGeneratorResponseModel {
  const range = DIFFICULTY_LEVEL_RANGE_MAP[difficultyLevel]
  const numerator = randomIntFromInterval(range.min, range.max, excludes)
  const options = shuffle<number | 'undefined'>(['undefined', 0, 1, numerator])

  return { numerator, options, variation: range.variation }
}

function useFractionDivideZero({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): UseFractionDivideZeroReturnType {
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [selectedAnswer, setSelectedAnswer] = useState<string | number>()
  const question = useMemo(
    () => getFractionDivideZeroQuestionData(difficultyLevel, taskHistory),
    [difficultyLevel, taskHistory],
  )

  const handleCheckAnswer = useCallback((): void => {
    if (!selectedAnswer && selectedAnswer !== 0) return
    onAnswerStateChange(selectedAnswer === 'undefined')
  }, [selectedAnswer, onAnswerStateChange])

  const onInputChange = useCallback((val?: string | number) => setSelectedAnswer(val), [])

  const goToNextQuestion = useCallback(() => {
    if (!question) return
    const newTaskHistory = [question.numerator, ...taskHistory]
    setTaskHistory(newTaskHistory.slice(0, question.variation * 0.7))
    onInputChange()
    onNextQuestionClick()
  }, [taskHistory, onInputChange, onNextQuestionClick, question])

  return {
    numerator: question.numerator,
    options: question.options,
    formValid: selectedAnswer !== undefined,
    selectedAnswer,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  } as const
}

export default useFractionDivideZero
